/**
 * notificationService.js
 * Firebase FCM Web Push — permission request, token registration, foreground listener
 */

import { messagingReady, getToken, onMessage, VAPID_KEY } from '../config/firebase';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// ─── Token decode (same pattern as api.js / NotificationContext) ───────────
const decodeToken = (encodedToken) => {
  try {
    return atob(atob(encodedToken));
  } catch {
    return null;
  }
};

const getAuthToken = () => {
  const encoded = localStorage.getItem('token');
  if (!encoded) return null;
  return decodeToken(encoded);
};

// ─── Browser / OS info ──────────────────────────────────────────────────────
const getDeviceName = () => {
  const ua = navigator.userAgent;
  let name = 'Unknown Browser';
  if (ua.includes('Edg')) name = 'Edge';
  else if (ua.includes('Chrome')) name = 'Chrome';
  else if (ua.includes('Firefox')) name = 'Firefox';
  else if (ua.includes('Safari')) name = 'Safari';
  const platform = navigator.platform || 'Unknown OS';
  return `${name} on ${platform}`;
};

// ─── Register FCM token with Backend ────────────────────────────────────────
const registerTokenWithBackend = async (fcmToken) => {
  const authToken = getAuthToken();
  if (!authToken) {
    console.warn('⚠️ FCM: No auth token found, skipping backend registration');
    return null;
  }

  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_FCM_TOKEN}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fcmToken,
      deviceType: 'web',
      deviceName: getDeviceName(),
    }),
  });

  if (!response.ok) {
    throw new Error(`Backend registration failed: ${response.status}`);
  }

  const result = await response.json();
  console.log('✅ FCM token registered with backend');
  return result;
};

// ─── Main: Request Permission + Get Token + Register ────────────────────────
/**
 * Call this after user logs in.
 * Requests browser notification permission, gets FCM token, registers it with backend.
 * @returns {string|null} FCM token or null if failed/denied
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.warn('⚠️ FCM: This browser does not support notifications');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ FCM: Service Worker not supported in this browser');
      return null;
    }

    /* Messaging is decided asynchronously (see config/firebase), and this runs
       from an effect on the first render, so it is waited for. Reading the
       variable straight off meant a refresh usually found it still null and
       gave up before asking for a token at all. */
    const fcm = await messagingReady;
    if (!fcm) {
      console.warn('⚠️ FCM: Firebase Messaging not supported in this browser');
      return null;
    }

    if (!VAPID_KEY) {
      console.warn('⚠️ FCM: VAPID key (REACT_APP_FIREBASE_VAPID_KEY) not set in .env');
      return null;
    }

    // Request browser permission
    const permission = await Notification.requestPermission();

    if (permission === 'denied') {
      console.warn('❌ FCM: Notification permission denied by user');
      return null;
    }

    if (permission !== 'granted') {
      console.log('⏸️ FCM: Notification permission dismissed');
      return null;
    }

    console.log('✅ FCM: Notification permission granted');

    // Register Service Worker (handles background notifications)
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    /* A registration is not an active worker yet, and a push subscription needs
       one — asking straight away failed with "no active Service Worker" the
       first time a browser ever loaded the app. */
    await navigator.serviceWorker.ready;
    console.log('✅ FCM: Service Worker registered and active');

    // Get FCM token
    const fcmToken = await getToken(fcm, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!fcmToken) {
      console.warn('⚠️ FCM: No token received from Firebase');
      return null;
    }

    console.log('✅ FCM: Token obtained');

    // Cache token locally
    localStorage.setItem('fcm_token', fcmToken);

    // Register with backend
    await registerTokenWithBackend(fcmToken);

    return fcmToken;
  } catch (error) {
    console.error('❌ FCM: Error in requestNotificationPermission:', error.message);
    return null;
  }
};

// ─── Remove Token on Logout ──────────────────────────────────────────────────
/**
 * Call this when user logs out.
 * Deactivates the FCM token in the backend so no more push notifications are sent.
 */
export const removeNotificationToken = async () => {
  try {
    const fcmToken = localStorage.getItem('fcm_token');
    const authToken = getAuthToken();

    if (!fcmToken || !authToken) {
      localStorage.removeItem('fcm_token');
      return;
    }

    await fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_FCM_TOKEN}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fcmToken }),
    });

    localStorage.removeItem('fcm_token');
    console.log('✅ FCM: Token removed from backend');
  } catch (error) {
    console.error('❌ FCM: Failed to remove token:', error.message);
    localStorage.removeItem('fcm_token');
  }
};

// ─── Foreground Message Listener ────────────────────────────────────────────
/**
 * Sets up listener for FCM messages when app is in foreground (tab is open).
 * Background notifications are handled by the service worker automatically.
 *
 * @param {function} onNotificationReceived - Callback with FCM payload
 * @returns {function} Unsubscribe function — call on cleanup/logout
 */
export const setupForegroundListener = (onNotificationReceived) => {
  /* The caller keeps what this returns and calls it on cleanup, so it stays a
     plain function even though messaging is settled asynchronously: the
     listener attaches when it is ready, and unsubscribing before that just
     stops it attaching at all. */
  let unsubscribe = null;
  let dropped = false;

  messagingReady.then((fcm) => {
    if (!fcm) {
      console.warn('⚠️ FCM: Messaging not supported, foreground listener skipped');
      return;
    }
    if (dropped) return;
    unsubscribe = onMessage(fcm, (payload) => {
      console.log('📬 FCM: Foreground notification received', payload);
      if (onNotificationReceived) {
        onNotificationReceived(payload);
      }
    });
  });

  return () => {
    dropped = true;
    if (unsubscribe) unsubscribe();
  };
};
